import React from "react";

/**
 * Small bar chart for a short, ordered series — weekly load, calories per day,
 * sleep per night.
 *
 * `null` values render as a muted "sin registro" bar rather than a zero-height
 * one, because a day nobody logged and a day of genuinely zero intake are
 * different facts and the club acts differently on each.
 *
 * `reference` draws the dashed threshold line the series should be read
 * against (the daily calorie reference, 150% of average load).
 *
 * The plot is an SVG stretched to the container (`preserveAspectRatio="none"`),
 * so anything that must keep its proportions — the labels, the line width —
 * lives outside that stretch: labels are HTML, the line uses a non-scaling stroke.
 */
const Bars = ({
  values = [],
  labels = [],
  reference = null,
  referenceLabel,
  height = 78,
  colorFor,
  label,
}) => {
  const numeric = values.filter((v) => typeof v === "number" && Number.isFinite(v) && v > 0);
  if (numeric.length === 0) {
    return <p className="py-4 text-[12px] text-peri-300">Sin registros en el periodo.</p>;
  }

  const max = Math.max(...numeric, reference ?? 0, 1);
  const slot = 100 / values.length;
  const plotHeight = height - 18;
  const baseline = plotHeight + 4;

  return (
    <div role="img" aria-label={label || "Serie por día"}>
      <svg
        viewBox={`0 0 100 ${baseline}`}
        width="100%"
        height={baseline}
        preserveAspectRatio="none"
        aria-hidden="true"
        className="block"
      >
        {values.map((value, index) => {
          const missing = typeof value !== "number" || !Number.isFinite(value) || value <= 0;
          // Missing days keep a 2px stub so the gap is visible as a gap rather
          // than as empty space the eye reads as the end of the series.
          const barHeight = missing ? 2 : (value / max) * plotHeight;
          const fill = missing ? "#e3e0e0" : colorFor ? colorFor(value) : "#4274d9";
          return (
            <rect
              key={index}
              x={index * slot + slot * 0.2}
              y={baseline - barHeight}
              width={slot * 0.6}
              height={barHeight}
              fill={fill}
            />
          );
        })}

        {reference !== null && reference > 0 && (
          <line
            x1="0"
            y1={baseline - (reference / max) * plotHeight}
            x2="100"
            y2={baseline - (reference / max) * plotHeight}
            stroke="#bc1d01"
            strokeWidth="1"
            strokeDasharray="4 3"
            vectorEffect="non-scaling-stroke"
          >
            {referenceLabel && <title>{referenceLabel}</title>}
          </line>
        )}
      </svg>

      <div
        className="mt-1 grid text-center text-[10px] leading-none text-peri-300"
        style={{ gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))` }}
        aria-hidden="true"
      >
        {values.map((_, index) => (
          <span key={index} className="truncate">
            {labels[index] ?? ""}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Bars;
